import { useRef, useCallback } from 'react';

interface SwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  minDistance?: number;
  maxVerticalRatio?: number;
}

/**
 * Hook for detecting horizontal swipe gestures on touch devices.
 * Returns touch event handlers to attach to a container element.
 * 
 * - Only triggers if horizontal distance exceeds `minDistance` (default 50px)
 * - Only triggers if the swipe is more horizontal than vertical
 * - Ignores slow drags by checking velocity (must complete within 500ms)
 */
export function useSwipeNavigation({
  onSwipeLeft,
  onSwipeRight,
  minDistance = 50,
  maxVerticalRatio = 0.75,
}: SwipeOptions) {
  const touchStart = useRef<{ x: number; y: number; time: number } | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current) return;

    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    const elapsed = Date.now() - touchStart.current.time;

    touchStart.current = null;

    // Must be a quick gesture (under 500ms) 
    if (elapsed > 500) return;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Must exceed minimum distance
    if (absDx < minDistance) return;

    // Must be primarily horizontal (vertical component < ratio of horizontal)
    if (absDy > absDx * maxVerticalRatio) return;

    if (dx < 0) {
      onSwipeLeft?.();
    } else {
      onSwipeRight?.();
    }
  }, [onSwipeLeft, onSwipeRight, minDistance, maxVerticalRatio]);

  return { onTouchStart, onTouchEnd };
}
